import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, effect, ElementRef, input, model, output, ViewChild } from '@angular/core';
import { MatCard, MatCardContent } from '@angular/material/card';
import { provideNativeDateAdapter } from '@angular/material/core';
import { CalendarDateFormatter, CalendarEvent, CalendarEventAction, CalendarEventTimesChangedEvent, CalendarModule, CalendarView } from 'angular-calendar';
import { addDays, addHours, addMinutes, differenceInMinutes, endOfDay, endOfMonth, endOfWeek, isEqual, set, startOfDay, startOfHour, startOfMonth, startOfWeek, subDays } from 'date-fns';
import { Subject } from 'rxjs';
import { MaterialModule } from 'src/app/material.module';
import { fromEvent } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { WeekViewHourSegment } from 'calendar-utils';
import { MatDialog } from '@angular/material/dialog';
import { tasksServices } from 'src/app/services/tasks.service';
import {
  firstValueFrom,
  map,
  debounceTime,
  switchMap,
  catchError,
  of,
  Observable,
  timer,
  Subscription,
} from 'rxjs';
import { ViewActivityComponent } from './view-activity/view-activity.component';
import { updateTime } from '../helper/date-format.helper';
import { userServices } from 'src/app/services/users.service';
import { googleCalendar } from 'src/app/services/google-calendar.service';
import { ActivatedRoute, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CalendarSkeletonLoaderComponent } from '../loading-models/loading-calendar';
import { UsersCacheService } from 'src/app/services/cache/users-cache';
import { activityTypeCache } from 'src/app/services/cache/activity-types-cache';
import { ActivityLeadService } from 'src/app/services/lead-activity.servise';
import { userDataService } from 'src/app/services/userDataService';
import { ActivityModalDataService } from 'src/app/services/lead-activity-modal.service';
import { FiltersComponent } from '@app/activities/filters/filters.component';

const colors: any = {
  red: {
    primary: '#e1eeff',
    secondary: '#e1eeff',
  },
  blue: {
    primary: '#e1eeff',
    secondary: '#e1eeff',
  },
  yellow: {
    primary: '#e1eeff',
    secondary: '#e1eeff',
  },
};

function floorToNearest(amount: number, precision: number) {
  return Math.floor(amount / precision) * precision;
}

function ceilToNearest(amount: number, precision: number) {
  return Math.ceil(amount / precision) * precision;
}

function lightenColor(hex: string, percent: number = 40): string {
  if (!hex || !hex.startsWith('#')) {
    return '#e1eeff';
  }

  hex = hex.replace('#', '');

  const r = parseInt(hex.substring(0, 2), 16);
  const g = parseInt(hex.substring(2, 4), 16);
  const b = parseInt(hex.substring(4, 6), 16);

  const newR = Math.round(r + (255 - r) * (percent / 100));
  const newG = Math.round(g + (255 - g) * (percent / 100));
  const newB = Math.round(b + (255 - b) * (percent / 100));

  return '#' + [newR, newG, newB]
    .map(x => x.toString(16).padStart(2, '0'))
    .join('');
}

function getEventColor(row: any, showCalendarMode: boolean, meetingScheduler: boolean): { primary: string, secondary: string } {
  if (!showCalendarMode || meetingScheduler) {
    return {
      primary: '#565961',
      secondary: '#565961'
    };
  }

  const baseColor = row.type?.color || '#0d68c5';

  if (row.completed) {
    const lightenedColor = lightenColor(baseColor, 40);
    return {
      primary: lightenedColor,
      secondary: lightenedColor
    };
  }

  return {
    primary: baseColor,
    secondary: baseColor
  };
}

export interface CustomCalendarEvent extends CalendarEvent {
  completed?: boolean;
  type?: any;
  id?: any
}


@Component({
  selector: 'app-calendar',
  standalone: true,
  imports: [
    CalendarModule,
    CommonModule,
    MaterialModule,
    ViewActivityComponent,
    FormsModule,
    CalendarSkeletonLoaderComponent,
    FiltersComponent
  ],
  templateUrl: './calendar.component.html',
  styleUrl: './calendar.component.scss',
  providers: [provideNativeDateAdapter(), CalendarDateFormatter],
})
export class CalendarComponent {
  @ViewChild('scrollContainer') scrollContainer: ElementRef<HTMLElement>;
  allowDragReschedule = input<boolean>(true);
  defaultView = model<any>();
  hourSegments = input<number>(4);
  dayStartHour = input<number>(0)
  dayEndHour = input<number>(23)
  showCalendarMode = input<boolean>();
  detailedInfo = input<any>(null);
  hideCalendarProps = input<boolean>(false);
  meetingScheduler = input<boolean>(false);
  
  // New inputs for filters integration
  activityTypesInput = input<any[]>([]);
  defaultSelectedTypeIds = input<number[]>([]);
  
  meetingDuration: string = "30"
  allScheduledMeetings: any[] = [];
  propasalTimes = input<CustomCalendarEvent[]>([]);
  defaultMeetingDuration = input<any>("30")
  private subscription!: Subscription;
  private filterSubscription: Subscription;
  private bulkUpdateSubscription: Subscription;
  appliedFilters: any[] = [];
  appliedTypeFilters: number[] = [];
  private initialFiltersApplied: boolean = false;

  loading: boolean = true;
  loadingTimeout: any;
  viewDate: Date = new Date();

  actions: CalendarEventAction[] = [
    {
      label: '<span >D</span>',
      onClick: ({ event }: { event: CustomCalendarEvent }): void => {
        this.events = this.events.filter((iEvent) => iEvent !== event);
        this.handleEvent('Deleted', event);
      },
    },
  ];
  activityTypes: any[] = [];
  users: any[] = [];

  events: CustomCalendarEvent[] = [];

  activeDayIsOpen = true;
  weekStartsOn: 0 = 0;
  dragToCreateActive = false;
  hasStartDetailedInfo = false;
  modalEvent: any;
  firstTimeScroll: boolean = false;

  calendarData = output<any>()
  viewChanged = output<string>()
  typeFilterChanged = output<any[]>()

  constructor(
    private cdr: ChangeDetectorRef,
    private dialog: MatDialog,
    private taskService: tasksServices,
    private usersService: userServices,
    private usersCacheService: UsersCacheService,
    private activityTypeCacheService: activityTypeCache,
    private route: ActivatedRoute,
    private router: Router,
    private googleCalendar: googleCalendar,
    private activityLeadService: ActivityLeadService,
    private userDataService: userDataService,
    private modalDataService: ActivityModalDataService
  ) {
    effect(() => {
      if (this.detailedInfo() && this.detailedInfo().startDate) {
        this.manageModalCalendar(this.detailedInfo())
      }
      if (this.propasalTimes().length) {
        this.events = this.events.concat(this.propasalTimes())
        this.allScheduledMeetings = this.allScheduledMeetings.concat(this.propasalTimes())
      }

      if (this.defaultMeetingDuration()) {
        this.meetingDuration = this.defaultMeetingDuration().toString()
      }
      
      // Use activity types from input if provided
      if (this.activityTypesInput()?.length) {
        this.activityTypes = this.activityTypesInput();
      }
    });
  }


  getEvents() {
  }

  async ngOnInit() {
    this.loading = true;
    this.startLoadingTimeout();

    const currentFilters = this.taskService.getCurrentFilters();
    if (currentFilters && currentFilters.length > 0) {
      const typeFilter = currentFilters.find(f => f.field === 'type');
      if (typeFilter) {
        this.appliedTypeFilters = typeFilter.value || [];
        this.appliedFilters = currentFilters.filter(f => f.field !== 'type');
      } else {
        this.appliedTypeFilters = [];
        this.appliedFilters = currentFilters;
      }
      this.initialFiltersApplied = true;
    }

    this.filterSubscription = this.taskService.filter$.subscribe((filters) => {
      if (filters && this.initialFiltersApplied) {
        this.applyFilters(filters);
      } else if (filters && !this.initialFiltersApplied) {
        this.initialFiltersApplied = true;
      }
    });

    this.route.queryParams.subscribe(async (params) => {
      const meet = params['meet'] === 'true';
      if (meet) {
        let user = await this.userDataService.getUserData();
        if (user && !user.GoogleTokens?.find(row => row.service === "MEET")) {
          user = { ...user, GoogleTokens: user.GoogleTokens.concat({ id: new Date(), service: "MEET" }) }
          await this.userDataService.setUserData(user)
        }
        this.router.navigate([], {
          relativeTo: this.route,
          queryParams: { showModal: null },
          queryParamsHandling: 'merge',
          replaceUrl: true,
        });
      }
    })

    try {
      this.activityTypeCacheService.fetchData();
      this.usersCacheService.fetchData();

      const [activityTypeCache, usersCache] = await Promise.all([
        firstValueFrom(this.activityTypeCacheService.data$),
        firstValueFrom(this.usersCacheService.data$),
      ]);

      // Only assign if not already provided via input
      if (!this.activityTypesInput()?.length) {
        this.activityTypes = activityTypeCache;
      }
      this.users = usersCache;

      await this.getData();
    } catch (error) {
      console.error('Error loading calendar data:', error);
    } finally {
      clearTimeout(this.loadingTimeout);
      this.loading = false;
      this.cdr.detectChanges();
    }

    this.subscription = this.taskService.result$.subscribe((result) => {
      if (!result)
        return
      if (result?.editMode) {
        this.getDetailedModalResult(result.data)
      } else {
        this.events = [...this.events,
        {
          ...result.data,
          start: new Date(result.data.startDate),
          end: new Date(result.data.endDate),
          color: getEventColor(result.data, this.showCalendarMode(), false),
          meta: {
            freeze: !this.showCalendarMode()
          },
          resizable: {
            beforeStart: this.showCalendarMode() && this.allowDragReschedule(),
            afterEnd: this.showCalendarMode() && this.allowDragReschedule(),
          },
          draggable: this.showCalendarMode() && this.allowDragReschedule(),
          isNew: false
        }];
        this.refresh()
      }
    });

    // Reload calendar on bulk recurring event updates
    this.bulkUpdateSubscription = this.modalDataService.activityEvents$.subscribe(event => {
      if (event?.action === 'bulk-update') {
        this.getData();
      }
    });
  }

  // Handle type filter changes from the filters component
  onTypeSelected(event: any) {
    this.typeFilterChanged.emit(event);
  }

  applyFilters(filters: any[]) {
    const typeFilter = filters.find(f => f.field === 'type');

    const newTypeFilters = typeFilter ? (typeFilter.value || []) : [];
    const newAdvancedFilters = filters.filter(f => f.field !== 'type');

    const typeFiltersChanged = JSON.stringify(this.appliedTypeFilters.sort()) !== JSON.stringify(newTypeFilters.sort());
    const advancedFiltersChanged = JSON.stringify(this.appliedFilters) !== JSON.stringify(newAdvancedFilters);
    const filtersChanged = typeFiltersChanged || advancedFiltersChanged;

    this.appliedTypeFilters = newTypeFilters;
    this.appliedFilters = newAdvancedFilters;

    if (filtersChanged && !this.loading) {
      this.getData();
    }
  }

  startLoadingTimeout() {
    this.loadingTimeout = setTimeout(() => {
      if (this.loading) {
        this.loading = false;
        this.cdr.detectChanges();
      }
    }, 15000);
  }

  onLoadData() {
    const { start, end } = this.getViewRange(this.defaultView(), this.viewDate);
    this.loading = true;
    this.startLoadingTimeout();
    this.cdr.detectChanges();

    const currentFilters = this.taskService.getCurrentFilters() || [];
    const typeFilter = currentFilters.find(f => f.field === 'type');
    const currentTypeFilters = typeFilter ? (typeFilter.value || []) : [];
    const currentAdvancedFilters = currentFilters.filter(f => f.field !== 'type');

    const isValidStart = start instanceof Date && !isNaN(start.getTime());
    const isValidEnd = end instanceof Date && !isNaN(end.getTime());

    const requestParams: any = {
      pageSize: 1000,
      filter: JSON.stringify(currentAdvancedFilters),
      userTimezone: Intl.DateTimeFormat().resolvedOptions().timeZone, // ← ADD
      ...(isValidStart && { startDate: start }),
      ...(isValidEnd && { endDate: end })
    };

    if (currentTypeFilters.length > 0) {
      requestParams.type = JSON.stringify(currentTypeFilters);
    }

    return this.taskService.getAllActivities(requestParams);
  }

  async getData() {
    this.loading = true;
    this.startLoadingTimeout();
    this.cdr.detectChanges();

    try {
      const records = await firstValueFrom(this.onLoadData());
      this.mapEvents(records.data);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      clearTimeout(this.loadingTimeout);
      this.loading = false;
      this.cdr.detectChanges();
    }
  }

  async manageModalCalendar(details) {
    const startTime = isEqual(updateTime(details.startDate, details.startTime), new Date(details.startDate.setHours(0, 0, 0, 0))) ? null : details.startTime

    const activityType = details.type || (this.activityTypes.length ? this.activityTypes[0] : null);
    const activityColor = activityType?.color || '#0d68c5';

    this.modalEvent = {
      ...details,
      id: details.editMode ? details.id : Date.now(),
      allDay: !startTime,
      start: startTime ? updateTime(details.startDate, startTime) : new Date(details.startDate.setHours(0, 0, 0, 0)),
      end: startTime && details.endTime ? updateTime(details.startDate, details.endTime) : startTime ? null : new Date(details.startDate.setHours(23, 59, 59, 999)),
      color: {
        primary: activityColor,
        secondary: activityColor,
      },
      meta: {
        tmpEvent: true,
        isModal: true,
        editMode: details.editMode
      },
      draggable: true,
      resizable: {
        beforeStart: true,
        afterEnd: true,
      },
    };

    if (!isEqual(startOfDay(this.viewDate), startOfDay(details.startDate))) {
      this.viewDate = details.startDate;
      await this.changeView()
      this.refresh()
    }

    let eventExist = this.events.find(row => row.meta?.isModal)
    if (!this.firstTimeScroll) {
      this.scrollToCurrentView();
      this.firstTimeScroll = true;
    }


    if (eventExist) {

      Object.assign(eventExist, this.modalEvent)
      this.hasStartDetailedInfo = true;
      this.refresh()
    } else if (this.modalEvent.meta?.editMode) {
      let existingEvent = this.events.find(row => row.id === this.modalEvent.id)
      if (existingEvent) {
        Object.assign(existingEvent, this.modalEvent)
        this.refresh()
      }

    } else {
      this.events = [...this.events, this.modalEvent];
      this.refresh()

    }
  }

  loadActivityTypes() {
    this.activityTypeCacheService.fetchData();
    this.activityTypeCacheService.data$.subscribe(activityType => {
      this.activityTypes = activityType;
    });
  }

  loadUsers() {
    this.usersCacheService.fetchData();
    this.usersCacheService.data$.subscribe(users => {
      this.users = users.map((row) => {
        return { id: row.id, name: row.name + ' ' + row.lastName, firstName: row.name, lastName: row.lastName };
      });
    });
  }

  mapEvents(data) {
    this.events = data.map(row => {

      return {
        ...row,
        start: row.startDate ? new Date(row.startDate) : null,
        end: row.endDate || row.allDay ? new Date(row.endDate) : null,
        allDay: row.allDay,
        color: getEventColor(row, this.showCalendarMode(), this.meetingScheduler()),
        meta: {
          freeze: !this.showCalendarMode() && !this.meetingScheduler()
        },
        resizable: {
          beforeStart: this.showCalendarMode() && !this.meetingScheduler() && this.allowDragReschedule(),
          afterEnd: this.showCalendarMode() && !this.meetingScheduler() && this.allowDragReschedule(),
        },
        draggable: this.showCalendarMode() && !this.meetingScheduler() && this.allowDragReschedule(),
        isNew: false
      }

    })
    if (this.modalEvent) {
      if (this.modalEvent.meta?.editMode) {
        this.events = this.events.filter(row => row.id !== this.modalEvent.id)
      }
      this.events.push(this.modalEvent);
      this.hasStartDetailedInfo = true;
    }
    const propsedTimes = this.allScheduledMeetings.filter(row => {
      const exist = this.events.filter(ev => ev.id === row.id);
      return exist.length ? false : true
    })
    this.events = this.events.concat(propsedTimes)

    this.loading = false;
    clearTimeout(this.loadingTimeout);
    this.refresh()
    this.scrollToCurrentView()
  }

  ngAfterViewInit() {
  }

  dayClicked({ date, events }: { date: Date; events: CustomCalendarEvent[] }): void {
  }

  hourSegmentClicked(date) {
  }


  eventTimesChanged({
    event,
    newStart,
    newEnd,
  }: CalendarEventTimesChangedEvent): void {
    const oldStart = new Date(event.start);
    const oldEnd = event.end ? new Date(event.end) : null;

    event.start = newStart;
    event.end = newEnd;

    this.refresh();

    if (event?.meta?.meetingScheduler) {
      this.handleEvent('update', event);
      return
    }

    const startChanged = oldStart.getTime() !== newStart.getTime();
    const endChanged = (!oldEnd && newEnd) ||
      (oldEnd && !newEnd) ||
      (oldEnd && newEnd && oldEnd.getTime() !== newEnd.getTime());

    if (startChanged || endChanged) {
      this.handleEvent('update', event);
    }
  }


  removeEvent(event) {
    this.events = this.events.filter(row => row.id !== event.id);
    this.allScheduledMeetings = this.allScheduledMeetings.filter(row => row.id !== event.id)
  }

  handleMeetingSchedulerEvent(event, editMode = false) {
    const startTime = event.start;

    const endTime: any = addMinutes(startTime, this.meetingDuration ? Number(this.meetingDuration) : 30);
    this.events = this.events.map(row => {
      if (row.id === event.id) {
        return { ...row, end: endTime };
      }
      return row;
    })
    this.refresh()


    if (editMode) {
      this.allScheduledMeetings.map(row => {
        if (row.id === event.id) {
          return { ...event, end: endTime };
        }
        return row;
      })
    } else {
      this.allScheduledMeetings.push({ ...event, end: endTime })
    }
  }

  onMeetingDurationChange(event) {
    this.events = this.events.filter(row => !row.meta?.tmpEvent);
    this.allScheduledMeetings = []
  }

  async handleEvent(action: string, event: CustomCalendarEvent, mouseEvent?: any) {

    if (event.meta?.meetingScheduler) {
      return this.handleMeetingSchedulerEvent(event, true)
    }

    if (event?.meta?.isModal) {
      return this.calendarData.emit(event)
    }
    if (event.meta?.freeze || event.meta?.meetingScheduler) {
      return;
    }

    if (action == 'update') {
      return await firstValueFrom(this.taskService.patch({ startDate: event.start, endDate: event.end }, event.id))
    } else if (action == 'view') {
      const dialogWidth = 350;
      const dialogRef = this.dialog.open(ViewActivityComponent, {
        width: `${dialogWidth}px`,
        height: `300px`,
        panelClass: 'hidden-dialog',
        data: { ...event, hourSegments: 60 / this.hourSegments(), activityTypes: this.activityTypes, users: this.users },
      });
      dialogRef.addPanelClass('custom-dialog-animation');
      dialogRef.afterOpened().subscribe(() => {
        setTimeout(() => {
          const dialogElement = dialogRef.componentInstance.dialogElement.nativeElement;

          if (dialogElement) {
            const actualDialogHeight = dialogElement.clientHeight;

            dialogRef.removePanelClass('hidden-dialog')
            dialogRef.updateSize(`${dialogWidth}px`, `${actualDialogHeight}px`);

            if (mouseEvent) {
              const updatedPosition = this.calculateBestPosition(mouseEvent, dialogWidth, actualDialogHeight);
              dialogRef.updatePosition(updatedPosition);
            }
          }
        }, 0);
      });
      dialogRef.afterClosed().subscribe((action) => {
        if (action?.type === 'delete') {
          this.events = this.events.filter(row => row.id !== event.id)
          this.refresh();
        } else if (action?.type === 'mark-complete') {
          this.events = this.events.map(row => {
            if (row.id === event.id) {
              const mergedData = {
                ...row,
                ...action.result?.data,
                color: getEventColor({ ...row, ...action.result?.data }, this.showCalendarMode(), false)
              };
              return mergedData;
            }
            return row;
          });

          this.refresh();
        }

      });
    }
  }

  getDetailedModalResult(result) {
    this.events = this.events.map(row => {
      if (row.id === result.id) {
        return {
          ...result,
          start: new Date(result.startDate),
          end: new Date(result.endDate),
          color: getEventColor(result, this.showCalendarMode(), false),
          meta: {
            freeze: !this.showCalendarMode()
          },
          resizable: {
            beforeStart: this.showCalendarMode() && this.allowDragReschedule(),
            afterEnd: this.showCalendarMode() && this.allowDragReschedule(),
          },
          draggable: this.showCalendarMode() && this.allowDragReschedule(),
          isNew: false
        }
      }
      return row;
    })
    this.modalEvent = null;
    this.refresh()
  }


  addEvent(): void {
  }

  deleteEvent(eventToDelete: CustomCalendarEvent): void {
    this.events = this.events.filter((event) => event !== eventToDelete);
  }

  changeView() {
    this.getData()
    if (this.modalEvent && this.modalEvent.meta?.isModal) {

      return this.calendarData.emit({
        ...this.modalEvent, start: set(this.modalEvent.start, {
          year: this.viewDate.getFullYear(),
          month: this.viewDate.getMonth(),
          date: this.viewDate.getDate(),
        })
      })
    }
  }

  setView(view: CalendarView): void {
    this.defaultView().set(view);
    this.getData()
    setTimeout(() => this.scrollToCurrentView(), 1110);
    this.cdr.detectChanges();

  }

  clickView(view: string) {
    this.defaultView.set(view)
    this.viewChanged.emit(view)
    this.getData()  // Add this line
  }



  private getViewRange(view: CalendarView, date: Date): { start: Date; end: Date } {
    switch (view) {
      case CalendarView.Day:
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
      case CalendarView.Week:
        return {
          start: startOfWeek(date),
          end: endOfWeek(date)
        };
      case CalendarView.Month:
        return {
          start: startOfMonth(date),
          end: endOfMonth(date)
        };
      default:
        return {
          start: startOfDay(date),
          end: endOfDay(date)
        };
    }
  }


  private scrollToCurrentView() {
    if (!this.scrollContainer?.nativeElement) return;

    if (this.defaultView() === CalendarView.Week || this.defaultView() === CalendarView.Day) {
      const now = this.modalEvent && this.modalEvent.start || new Date();

      const dayStart = new Date(now.getFullYear(), now.getMonth(), now.getDate(), this.dayStartHour(), 0, 0);
      const minutesSinceDayStart = differenceInMinutes(startOfHour(now), dayStart);

      const hourHeight = 60;
      const totalScrollHeight = (minutesSinceDayStart * this.hourSegments()) / 4;

      const headerHeight = this.defaultView() === CalendarView.Week ? 60 : 0;

      this.scrollContainer.nativeElement.scrollTop = Math.max(totalScrollHeight + headerHeight, 0);
    }
  }


  async startDragToCreate(
    segment: WeekViewHourSegment,
    mouseDownEvent: MouseEvent,
    segmentElement: HTMLElement
  ) {
    const firstActivityType = this.activityTypes.length ? this.activityTypes[0] : null;
    const activityColor = firstActivityType?.color || '#0d68c5';

    const dragToSelectEvent: CustomCalendarEvent = {
      id: new Date(),
      title: firstActivityType?.name || 'New event',
      start: segment.date,
      type: firstActivityType,
      color: {
        primary: activityColor,
        secondary: activityColor,
      },
      meta: {
        tmpEvent: true,
        isModal: !this.showCalendarMode(),
        meetingScheduler: this.meetingScheduler()
      },
      resizable: {
        beforeStart: this.meetingScheduler() ? false : this.allowDragReschedule(),
        afterEnd: this.meetingScheduler() ? false : this.allowDragReschedule(),
      },
      draggable: this.allowDragReschedule(),
    };

    if (dragToSelectEvent.meta?.isModal) {
      return this.calendarData.emit(dragToSelectEvent);
    }

    this.events = [...this.events, dragToSelectEvent];
    const segmentPosition = segmentElement.getBoundingClientRect();
    this.dragToCreateActive = true;
    const endOfView = endOfWeek(this.viewDate, {
      weekStartsOn: this.weekStartsOn,
    });

    const dragObservable = fromEvent(document, 'mousemove', { passive: true })
      .pipe(
        takeUntil(fromEvent(document, 'mouseup'))
      );

    const dragSubscription = dragObservable.subscribe((mouseMoveEvent: MouseEvent) => {
      const minutesDiff = ceilToNearest(
        mouseMoveEvent.clientY - segmentPosition.top,
        60 / this.hourSegments()
      );

      const daysDiff =
        floorToNearest(
          mouseMoveEvent.clientX - segmentPosition.left,
          segmentPosition.width
        ) / segmentPosition.width;

      const newEnd = addDays(addMinutes(segment.date, minutesDiff), daysDiff);
      if (newEnd > segment.date && newEnd < endOfView) {
        dragToSelectEvent.end = newEnd;
      }
      this.refresh();
    });

    try {
      await firstValueFrom(fromEvent(document, 'mouseup'));

      dragSubscription.unsubscribe();

      if (this.meetingScheduler()) {
        return this.handleMeetingSchedulerEvent(dragToSelectEvent);
      }


      const dialogRef = await this.activityLeadService.openActivityOnlyModal({
        ...dragToSelectEvent,
        hourSegments: 60 / this.hourSegments(),
        activityTypes: this.activityTypes,
        users: this.users,
        detailed: true
      });

      dialogRef.afterClosed().subscribe((result) => {
        this.afterCloseModal(dragToSelectEvent, result);
      });


    } catch (error) {
      console.error('Error in drag operation:', error);
      dragSubscription.unsubscribe();
      this.events = this.events.filter(event => event.id !== dragToSelectEvent.id);
      this.refresh();
    } finally {
      this.dragToCreateActive = false;
    }
  }

  async openActivityDetailsModal(dragToSelectEvent) {
    const dialogRef = await this.activityLeadService.openActivityOnlyModal({ ...dragToSelectEvent, hourSegments: 60 / this.hourSegments(), activityTypes: this.activityTypes, users: this.users, detailed: true })
    dialogRef.afterClosed().subscribe((result) => {
      this.afterCloseModal(dragToSelectEvent, result);
    });


  }

  afterCloseModal(dragToSelectEvent, result) {
    if (result == "details") {
      this.openActivityDetailsModal(dragToSelectEvent)
    }
    else if (result) {
      let activityType = result.type;
      if (!activityType && result.type_id) {
        activityType = this.activityTypes.find(t => t.id === result.type_id);
      }

      const activityColor = activityType?.color || '#0d68c5';

      Object.assign(dragToSelectEvent, result, {
        start: result.startDate ? new Date(result.startDate) : null,
        end: result.endDate ? new Date(result.endDate) : null,
        allDay: !result.startDate,
        type: activityType,
        color: {
          primary: activityColor,
          secondary: activityColor
        }
      });
      delete dragToSelectEvent.meta.tmpEvent;

      this.refresh()
    } else {
      this.events = this.events.filter(
        (event) => event.id !== dragToSelectEvent.id
      );
    }
    this.dragToCreateActive = false;
    this.refresh();

  }

  calculateBestPosition(
    mouseEvent: MouseEvent,
    dialogWidth: number,
    dialogHeight: number
  ): { top: string; left: string } {
    const offset = 10;
    const { clientX: mouseX, clientY: mouseY } = mouseEvent;
    const { innerWidth: viewportWidth, innerHeight: viewportHeight } = window;

    let top: number = mouseY + offset;
    let left: number = mouseX + offset;

    if (mouseX - dialogWidth - offset > 0 && mouseY - dialogHeight - offset > 0) {
      left = mouseX - dialogWidth - offset;
      top = mouseY - dialogHeight - offset;
    }
    else if (mouseY - dialogHeight - offset > 0) {
      top = mouseY - dialogHeight - offset;
    }
    else {
      if (mouseX + dialogWidth + offset <= viewportWidth) {
        left = mouseX + offset;
      } else if (mouseX - dialogWidth - offset > 0) {
        left = mouseX - dialogWidth - offset;
      } else {
        left = Math.max(offset, Math.min(mouseX + offset, viewportWidth - dialogWidth - offset));
      }

      if (mouseY + dialogHeight + offset <= viewportHeight) {
        top = mouseY + offset;
      } else if (mouseY - dialogHeight - offset > 0) {
        top = mouseY - dialogHeight - offset;
      } else {
        top = Math.max(offset, Math.min(mouseY + offset, viewportHeight - dialogHeight - offset));
      }
    }

    return { top: `${top}px`, left: `${left}px` };
  }




  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    if (this.filterSubscription) {
      this.filterSubscription.unsubscribe();
    }
    if (this.bulkUpdateSubscription) {
      this.bulkUpdateSubscription.unsubscribe();
    }
    clearTimeout(this.loadingTimeout);
  }


  private refresh() {
    this.events = [...this.events];
    this.cdr.detectChanges();

  }

}